// src/components/common/SeoWrapper.tsx
//
// Keeps <head> in step with the route: title, description, canonical URL,
// Open Graph / Twitter cards, robots hint, and structured data on the home
// page. No dependency needed — a handful of meta tags updated on navigation.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { metaForPath, OG_IMAGE_PATH } from '../../config/seo';
import { brand } from '../../config/brand';

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, data: object | null): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
}

const SeoWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = metaForPath(pathname);
    const origin = `https://${brand.domain}`;
    const url = `${origin}${pathname === '/' ? '/' : pathname.replace(/\/$/, '')}`;
    const image = `${origin}${OG_IMAGE_PATH}`;

    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('name', 'robots', meta.noindex ? 'noindex, nofollow' : 'index, follow');
    upsertLink('canonical', url);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', brand.fullProductName);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    upsertMeta('name', 'twitter:image', image);

    upsertJsonLd(
      'ld-software',
      pathname === '/'
        ? {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: brand.fullProductName,
            applicationCategory: 'DeveloperApplication',
            operatingSystem: 'Windows, macOS, Linux',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            publisher: { '@type': 'Organization', name: brand.legal.companyName, url: brand.legal.website },
            url: origin,
            image,
            description: meta.description,
          }
        : null,
    );
  }, [pathname]);

  return <>{children}</>;
};

export default SeoWrapper;
